// Track CRUD interface. In demo mode this reads tracks/manifest.json
// and fetches each listed file. When Supabase is configured this swaps
// to a real `tracks` table with RLS-gated read access for public tracks
// + owner-only writes.
//
// Public surface:
//   listTracks()  → Promise<TrackSummary[]>
//   getTrack(id)  → Promise<Track>      (the full schema-shaped object)
//
// TrackSummary = { id, name, author, description, updatedAt }

import { isConfigured } from './config.js';
import { loadTrack } from '../../shared/track-schema.js';

// Tracks-folder base URL. Defaults to '../tracks/' for the dev path
// where the page is at `/web/index.html` and tracks live one level up.
// The production bundle (build.mjs) sets `globalThis.__RM_TRACKS_BASE__`
// to './tracks/' on the dist HTML so the deploy artifact is
// self-contained.
const TRACKS_DIR = globalThis.__RM_TRACKS_BASE__ ?? '../tracks/';
const MANIFEST_PATH = `${TRACKS_DIR}manifest.json`;

// Manifest changes rarely; cache it for the session. Track files are
// NOT cached — when the user re-exports a track and replaces the
// file on disk, they expect a reload to show the changes. An
// in-memory cache would silently serve the old version. Track files
// are small (the largest are ~1 MB) and refetching is cheap.
let manifestCache = null;

export async function listTracks() {
  if (isConfigured()) {
    // TODO: real Supabase query goes here.
    throw new Error('Supabase client not implemented yet.');
  }
  const manifest = await loadManifest();
  // Manifest entries can be objects ({file, name, author, description})
  // or bare filename strings (legacy). Object entries skip the per-
  // file fetch entirely — important because a track JSON can be 1 MB+
  // when a background image is inlined. String entries fall back to
  // fetching the file just to extract the summary fields.
  const summaries = await Promise.all(
    manifest.tracks.map(async entry => {
      const filename = filenameOf(entry);
      const id = idFromFilename(filename);
      if (typeof entry === 'object' && entry) {
        return {
          id,
          name: entry.name || id,
          author: entry.author || '',
          description: entry.description || '',
          updatedAt: entry.updatedAt ?? null,
        };
      }
      try {
        const data = await fetchTrackFile(id, filename);
        return summarize(id, data);
      } catch (err) {
        console.error(`Failed to load track "${filename}":`, err);
        return null;
      }
    }),
  );
  return summaries.filter(Boolean);
}

export async function getTrack(id) {
  if (isConfigured()) {
    throw new Error('Supabase client not implemented yet.');
  }
  const manifest = await loadManifest();
  const entry = manifest.tracks.find(e => idFromFilename(filenameOf(e)) === id);
  if (!entry) throw new Error(`Unknown track: ${id}`);
  const data = await fetchTrackFile(id, filenameOf(entry));
  const { track } = loadTrack(data);
  return track;
}

function filenameOf(entry) {
  return typeof entry === 'string' ? entry : entry?.file ?? '';
}

async function loadManifest() {
  if (manifestCache) return manifestCache;
  // Cache-bust + no-store: without this, the browser's HTTP cache can
  // serve a stale manifest after files were renamed on disk, listing
  // filenames that 404 silently and leaving the list empty.
  const res = await fetch(`${MANIFEST_PATH}?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`Failed to load tracks manifest: ${res.status}`);
  const data = await res.json();
  if (!Array.isArray(data?.tracks)) {
    throw new Error('Tracks manifest is malformed: missing "tracks" array');
  }
  manifestCache = data;
  return data;
}

async function fetchTrackFile(_id, filename) {
  // `?t=` cache-busts the browser's HTTP cache. python3 -m http.server
  // doesn't send Cache-Control, and the browser's heuristic cache
  // may keep serving an old `midohio.track.json` even after the user
  // has updated the file on disk. The query param forces a fresh
  // fetch every time. `cache: 'no-store'` belt-and-suspenders.
  const url = `${TRACKS_DIR}${filename}?t=${Date.now()}`;
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function idFromFilename(filename) {
  return filename.replace(/\.track\.json$/, '').replace(/\.json$/, '');
}

function summarize(id, data) {
  return {
    id,
    name: data.name || id,
    author: data.author || '',
    description: data.description || '',
    updatedAt: data.metadata?.modifiedAt ?? null,
  };
}

// Selected-track handoff. Lives in sessionStorage so the URL stays
// clean (no `?track=<id>` query string) — same convention as the
// roster picker and the editor's session-staged track. Survives
// in-tab refresh + back/forward; opening the URL fresh in a new tab
// will not have a track selected and the receiving page should fall
// back to "no track" UI.
const SELECTED_TRACK_KEY = 'race-manager:selected-track';
export function setSelectedTrack(id) {
  if (typeof id !== 'string' || !id) return;
  try { sessionStorage.setItem(SELECTED_TRACK_KEY, id); } catch {}
}
export function getSelectedTrack() {
  try { return sessionStorage.getItem(SELECTED_TRACK_KEY) || null; } catch { return null; }
}
export function clearSelectedTrack() {
  try { sessionStorage.removeItem(SELECTED_TRACK_KEY); } catch {}
}
